import { flagEmojiOf, isCountryCode, type CountryCode } from '@aysar/contracts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfirm } from '@/shared/hooks/useConfirm/useConfirm';

import { useCountrySelectLogic } from '../CountrySelect/useCountrySelectLogic';
import { type SelectionOption } from '../types/selection-option.types';

import { type CountryChip } from './CountrySetField.types';

export interface UseCountrySetFieldLogicInput {
  value: readonly CountryCode[];
  onValueChange: (value: CountryCode[]) => void;
  isDisabled: boolean;
  isReadOnly: boolean;
}

export interface UseCountrySetFieldLogic {
  /** The chosen countries, ordered the way the picker orders them (collated by name). */
  chips: readonly CountryChip[];
  isEmpty: boolean;
  /** Chips can be taken off; the picker can add. Off when disabled or read-only. */
  isEditable: boolean;
  options: readonly SelectionOption[];
  searchPlaceholder: string;
  emptyLabel: string;
  /** What the picker answers with — the codes among its strings. */
  onPicked: (values: string[]) => void;
  /** Takes one chip off; the LAST one only after the operator has confirmed the policy widens. */
  remove: (code: CountryCode) => Promise<void>;
}

/**
 * A set of countries drawn as flag chips over a picker.
 *
 * The rail is the field's own (not `MultiSelect`'s), because a country is
 * recognised by its flag before its name and the kit's plain chip carries no
 * room for one. The last chip is guarded: an empty set does not mean "no
 * country" — it means **any** country, the widest policy there is — and a
 * control whose most permissive state is one careless click away needs to say
 * so before it gets there.
 */
export function useCountrySetFieldLogic({
  value,
  onValueChange,
  isDisabled,
  isReadOnly,
}: UseCountrySetFieldLogicInput): UseCountrySetFieldLogic {
  const { t } = useTranslation('uiKit');
  const { confirm } = useConfirm();
  const countries = useCountrySelectLogic();

  // The picker's list is already collated for the locale; walking it keeps the
  // rail in the same order without a second collation.
  const chips = useMemo<readonly CountryChip[]>(() => {
    const chosen = new Set<string>(value);
    return countries.options
      .filter((option) => chosen.has(option.value))
      .map((option) => {
        const code = option.value as CountryCode;
        return { code, flag: flagEmojiOf(code), label: option.label };
      });
  }, [countries.options, value]);

  const onPicked = useCallback(
    (values: string[]) => {
      onValueChange(values.filter(isCountryCode));
    },
    [onValueChange],
  );

  const remove = useCallback(
    async (code: CountryCode) => {
      if (value.length === 1) {
        const accepted = await confirm({
          title: t('countrySetField.lastCountry.title'),
          description: t('countrySetField.lastCountry.message'),
          confirmLabel: t('countrySetField.lastCountry.confirm'),
          tone: 'warning',
        });
        if (!accepted) return;
      }
      onValueChange(value.filter((chosen) => chosen !== code));
    },
    [confirm, onValueChange, t, value],
  );

  return {
    chips,
    isEmpty: value.length === 0,
    isEditable: !isDisabled && !isReadOnly,
    options: countries.options,
    searchPlaceholder: countries.searchPlaceholder,
    emptyLabel: countries.emptyLabel,
    onPicked,
    remove,
  };
}
